import { Divider, Drawer, Empty, Space, Table, Tag, Typography } from 'antd'
import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import dayjs from 'dayjs'
import { gold, red } from '@ant-design/colors'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleExclamation, faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons'
import { useBatchStatusQuery } from '../../../domain/invoicing/invoicingQueries'
import { BatchJobStatus, BatchStatus } from '../../../domain/invoicing/invoicingApi'
import { isBatchProcessing, isBatchDone, batchStatusConfig, jobStatusConfig } from '../BatchCreate/batchInvoicingModel'
import BatchProgressBanner from '../BatchCreate/BatchProgressBanner'
import { BatchMeta } from './batchMeta'
import { batchDrawerTitleSegments } from './batchDrawerTitleModel'
import { getBatchTypeCopy } from '../batchTypeCopy'
import { BatchJobListRow, groupBatchJobsByStatement, statementRowKeys } from './batchJobRowsModel'
import StatementJobBadge from '../StatementJobBadge'

type Props = {
  batchId: string | null
  batchMeta?: BatchMeta
  onClose: () => void
}

const BatchJobsDrawer = ({ batchId, batchMeta, onClose }: Props) => {
  const copy = getBatchTypeCopy(batchMeta?.batchType ?? 'create')
  const { data: status, isLoading } = useBatchStatusQuery(batchId)

  const isProcessing = isBatchProcessing(status?.status)
  const isDone = status && !isLoading && isBatchDone(status.status)
  const hasNoJobs = isDone && status.jobs.length === 0

  const rows = useMemo(() => groupBatchJobsByStatement(status?.jobs ?? []), [status?.jobs])
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([])
  const autoExpandedBatchId = useRef<string | null>(null)
  useEffect(() => {
    if (!batchId) {
      autoExpandedBatchId.current = null
      return
    }
    if (autoExpandedBatchId.current !== batchId && rows.length > 0) {
      setExpandedRowKeys(statementRowKeys(rows))
      autoExpandedBatchId.current = batchId
    }
  }, [batchId, rows])

  return (
    <Drawer
      open={!!batchId}
      onClose={onClose}
      placement="bottom"
      height="95%"
      closable={{ placement: 'end' }}
      title={<DrawerTitle batchMeta={batchMeta} status={status} />}
    >
      {isProcessing && batchId && (
        <BatchProgressBanner batchId={batchId} onDismiss={() => {}} batchType={batchMeta?.batchType ?? 'create'} />
      )}
      {hasNoJobs
        ? <Empty
            description={<div>
              <div><strong>{copy.emptyTitle}</strong></div>
              <div>{copy.emptyDescription}</div>
            </div>}
            style={{ marginTop: 64 }}
          />
        : <Table<BatchJobListRow>
            size="small"
            loading={isLoading}
            dataSource={rows}
            rowKey={row => row.key}
            expandable={{
              expandedRowKeys,
              onExpandedRowsChange: keys => setExpandedRowKeys(keys as string[]),
              rowExpandable: row => row.rowType === 'statement'
            }}
            columns={[
              {
                title: 'Customer',
                render: (_, row) => {
                  if (row.rowType === 'statement') {
                    return row.customerName
                  }
                  return row.isChild ? '' : row.job.customerName
                }
              },
              {
                title: 'Job',
                render: (_, row) => row.rowType === 'statement'
                  ? <StatementJobBadge count={row.jobCount} />
                  : row.job.jobName
              },
              {
                title: 'Status',
                render: (_, row) => row.rowType === 'statement' ? null : <JobStatusTag status={row.job.status} />
              },
              {
                title: 'Message',
                render: (_, row) => {
                  if (row.rowType === 'statement') {
                    return null
                  }
                  if (!row.job.message) {
                    return '-'
                  }
                  if (row.job.messageType !== 'error' && row.job.messageType !== 'warning') {
                    return <Typography.Text type="secondary">{row.job.message}</Typography.Text>
                  }
                  return <Space style={{ color: row.job.messageType === 'error' ? red[7] : gold[7] }}>
                    <FontAwesomeIcon icon={row.job.messageType === 'error' ? faCircleExclamation : faTriangleExclamation} />
                    {row.job.message}
                  </Space>
                }
              },
            ]}
            pagination={false}
            locale={{ emptyText: 'No jobs found for this batch.' }}
          />
      }
    </Drawer>
  )
}

const DrawerTitle = ({ batchMeta, status }: { batchMeta?: BatchMeta, status?: BatchStatus }) => {
  const createdLabel = batchMeta
    ? dayjs(batchMeta.createdAt).format('MMM D, YYYY h:mm A')
    : 'Batch Details'

  const batchStatus = status?.status
  const segments = batchDrawerTitleSegments(batchMeta, status)

  return (
    <div>
      <div style={{ fontWeight: 600 }}>{createdLabel}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginTop: 2 }}>
        {segments.map((segment, index) => (
          <Fragment key={segment}>
            {index > 0 && <Divider type="vertical" />}
            <Typography.Text type="secondary" style={{ fontSize: 13 }}>
              {segment}
            </Typography.Text>
          </Fragment>
        ))}
        {batchStatus && (
          <>
            {segments.length > 0 && <Divider type="vertical" />}
            <BatchStatusTag status={batchStatus} />
          </>
        )}
      </div>
    </div>
  )
}

const BatchStatusTag = ({ status }: { status: BatchStatus['status'] }) => {
  const config = batchStatusConfig[status]
  return <Tag color={config.color} style={{ marginInlineEnd: 0 }}>{config.label}</Tag>
}

const JobStatusTag = ({ status }: { status: BatchJobStatus['status'] }) => {
  const config = jobStatusConfig[status]
  return <Tag color={config.color}>{config.label}</Tag>
}

export default BatchJobsDrawer
